import { memo, useContext, useEffect, useState, useMemo } from "react";
import { fetchMerchandisingApi } from "app/pages/Merchandising/merchandisingActionCreators";
import PropTypes from "prop-types";
import AppGlobalsContext from "app/AppGlobalsContext";
import { computedProductDates, sortProducts } from "app/utils/utils";
import ProductListBlock from "app/pages/.shared/DynamicBlocks/blocks/ProductListBlock/ProductListBlock";
import { fetchProductsApi } from "app/pages/Listing/productActionCreators";
import { useIntl } from "react-intl";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { stringify } from "qs";
import { shouldHideMerchProductPriceBanner } from "app/pages/Merchandising/merchandisingSelector";
import { FilterProvider, OPTION_MODE } from "app/features/product/productFilters";
import { PRODUCTS_FILTERS_KEY, PRODUCT_EXPERIENCES } from "app/constants";

const MAX_PRODUCTS = 3;

const ProductListBlockContainer = props => {
	const { merchCode, shortVersion, shortVersionFeaturedProductUris = [] } = props;
	const [products, setProducts] = useState([]);
	const { formatMessage, formatDate } = useIntl();

	const [shouldHideProductPriceBanner, setHideProductPriceBanner] = useState(false);

	const [title, setTitle] = useState(formatMessage({ id: "products.carousel.listing.title" }));
	const { shop, partnerCode, productsVisibility, resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const redirectUrl = merchCode
		? {
				pathname: "/merch",
				search: `?${stringify({ code: merchCode })}`,
		  }
		: {
				pathname: "/listing",
		  };

	useEffect(() => {
		if (merchCode) {
			fetchMerchandisingApi({ code: merchCode, shop, productsVisibility, partnerCode }).then(
				response => {
					setTitle(response?.data?.merchandising?.label);

					const merchProductList = computedProductDates(response?.data?.products);
					let merchCatalogProductList = response?.data?.catalogProducts || [];
					merchCatalogProductList = computedProductDates(merchCatalogProductList);
					const finalProductList = merchProductList.concat(merchCatalogProductList);

					setProducts(finalProductList);
					setHideProductPriceBanner(
						shouldHideMerchProductPriceBanner(response?.data?.merchandising)
					);
				}
			);
		} else {
			fetchProductsApi({ shop, productsVisibility, partnerCode })
				.then(response => {
					let newProductList = computedProductDates(response?.data?.products);

					setProducts(newProductList);
				})
				.catch(error => {
					// eslint-disable-next-line no-console
					console.error(error);
				});
		}
	}, [merchCode, shop, productsVisibility, partnerCode, shortVersion]);

	let finalProductList = [...products];

	if (shortVersion) {
		if (shortVersionFeaturedProductUris?.length > 0) {
			const shortVersionFeaturedProducts = products.filter(product =>
				shortVersionFeaturedProductUris.includes(product.uri)
			);

			if (shortVersionFeaturedProducts?.length > 0) {
				finalProductList = sortProducts({
					products: shortVersionFeaturedProducts,
				}).slice(0, MAX_PRODUCTS);
			} else {
				finalProductList = products.slice(0, MAX_PRODUCTS);
			}
		} else {
			finalProductList = products.slice(0, MAX_PRODUCTS);
		}
	}

	const shouldShowMultiStageJourneyOption = finalProductList?.some(product =>
		[
			PRODUCT_EXPERIENCES.CIRCUIT,
			PRODUCT_EXPERIENCES.AUTOTOUR,
			PRODUCT_EXPERIENCES.CRUISE,
		].includes(product?.productExperience)
	);

	const facetConfigs = useMemo(
		() => [
			{
				field: PRODUCTS_FILTERS_KEY.DESTINATION,
				title: formatMessage({ id: "product.page.filter.destinations.label" }),
				valueGetter: item => item.country,
				...(shouldShowMultiStageJourneyOption && {
					buildOptions: () => {
						return [
							{
								value: "multi-stage-journey",
								label: formatMessage({
									id: "listing.filter.destination.multi.destinations.label",
								}),
							},
						];
					},
					optionMode: OPTION_MODE.MERGE,
					customMatcher: product => {
						return [
							PRODUCT_EXPERIENCES.CIRCUIT,
							PRODUCT_EXPERIENCES.AUTOTOUR,
							PRODUCT_EXPERIENCES.CRUISE,
						].includes(product?.productExperience);
					},
				}),
			},
			{
				field: PRODUCTS_FILTERS_KEY.TOPIC,
				title: formatMessage({ id: "listing.filter.theme.label" }),
				valueGetter: item => item.topics,
			},
			{
				field: PRODUCTS_FILTERS_KEY.MONTH,
				title: formatMessage({ id: "listing.filter.month.label" }),
				valueGetter: item => item.months.map(month => month.label),
				formatLabel: ({ label }) => {
					const [year, month] = label?.split("/");
					const date = new Date(Number(year), Number(month) - 1, 1);
					return formatDate(date, { month: "long", year: "numeric" });
				},
				customSort: (a, b) => {
					return a?.value?.localeCompare(b?.value);
				},
			},
		],
		[shouldShowMultiStageJourneyOption]
	);

	if (finalProductList.length === 0) {
		return null;
	}

	return (
		<FilterProvider products={finalProductList} facetConfigs={facetConfigs}>
			<ProductListBlock
				{...props}
				title={title}
				redirectUrl={redirectUrl}
				isMobile={isMobile}
				shouldHideProductPriceBanner={shouldHideProductPriceBanner}
			/>
		</FilterProvider>
	);
};

ProductListBlockContainer.propTypes = {
	merchCode: PropTypes.string,
	shortVersion: PropTypes.bool,
	shortVersionFeaturedProductUris: PropTypes.array,
	displayFilters: PropTypes.bool,
};

export default memo(ProductListBlockContainer);
